import React from "react";
import { BUDGET_BANDS, LEAD_TIME_BANDS, RESPONSE_BANDS, TIER_BANDS, fmtHours, pct } from "./labels";

function Big({ label, value, sub }) {
  return (
    <div className="border border-clay-200 bg-clay-50 p-4">
      <div className="smallcaps">{label}</div>
      <div className="font-serif text-3xl mt-1 tabular">{value}</div>
      {sub && <div className="text-[11px] text-clay-600 mt-1">{sub}</div>}
    </div>
  );
}

function BandRow({ labels, rows }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {Object.entries(labels).map(([k, lab]) => {
        const r = rows?.[k];
        if (!r) return null;
        const tone = r.conversion == null ? "text-clay-500" : r.conversion >= 30 ? "text-emerald-800" : r.conversion >= 15 ? "text-amber-800" : "text-terracotta";
        return (
          <span key={k} className="border border-clay-200 bg-white px-2 py-1 text-[11px]" title={`${r.sold} vendidos · ${r.not_sold} no vendidos`}>
            {lab}: <span className={`font-semibold ${tone}`}>{pct(r.conversion)}</span> <span className="text-clay-500">({r.leads})</span>
          </span>
        );
      })}
    </div>
  );
}

export function Comparison({ stats }) {
  if (!stats) return null;
  const c = stats.company;
  const lossLabels = stats.loss_reason_labels || {};
  return (
    <section className="space-y-4" data-testid="kimkim-comparison">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Big label="Leads" value={c.leads} sub={`${c.sold} vendidos · ${c.not_sold} no · ${c.open} en curso`}/>
        <Big label="Conversión" value={pct(c.conversion)} sub={c.pre_cohort_sold ? `vendidos / cerrados · sin los ${c.pre_cohort_sold} leads de antes de 2026` : "vendidos / cerrados"}/>
        <Big label="1ª respuesta (mediana)" value={fmtHours(c.median_first_response_hours)}/>
        <Big label="Mensajes (mediana)" value={c.median_messages ?? "—"}/>
        <Big label="Días hasta cierre" value={c.median_days_to_close ?? "—"} sub="mediana"/>
        <Big label="Analizados con IA" value={c.ai_analyzed} sub={Object.keys(c.loss_reasons || {}).length ? Object.entries(c.loss_reasons).sort((a, b) => b[1] - a[1]).slice(0, 2).map(([k, n]) => `${lossLabels[k] || k} ${n}`).join(" · ") : "—"}/>
      </div>
      <div className="border border-clay-300 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="kimkim-agents-table">
          <thead className="bg-clay-100 border-b border-clay-300 text-left text-[11px] uppercase tracking-wider text-clay-700">
            <tr>
              <th className="px-3 py-2 font-medium">Agente</th>
              <th className="px-3 py-2 font-medium text-right">Leads</th>
              <th className="px-3 py-2 font-medium text-right">Vend.</th>
              <th className="px-3 py-2 font-medium text-right">No</th>
              <th className="px-3 py-2 font-medium text-right">Curso</th>
              <th className="px-3 py-2 font-medium text-right">Conv.</th>
              <th className="px-3 py-2 font-medium text-right">1ª resp.</th>
              <th className="px-3 py-2 font-medium text-right">Msgs</th>
              <th className="px-3 py-2 font-medium text-right">Días cierre</th>
              <th className="px-3 py-2 font-medium">Conversión por categoría KimKim · presupuesto · antelación · respuesta</th>
            </tr>
          </thead>
          <tbody>
            {[{ ...c, agent_name: "Toda la empresa", agent_email: "__all__" }, ...stats.agents].map((a) => (
              <tr key={a.agent_email} className={`border-t border-clay-200 align-top ${a.agent_email === "__all__" ? "bg-clay-50 font-semibold" : ""}`} data-testid={`kimkim-agent-row-${a.agent_email || "none"}`}>
                <td className="px-3 py-2 whitespace-nowrap">{a.agent_name}{a.specialists?.length ? <div className="text-[11px] text-clay-500 font-normal">{a.specialists.join(", ")}</div> : null}</td>
                <td className="px-3 py-2 text-right tabular">{a.leads}</td>
                <td className="px-3 py-2 text-right tabular">{a.sold}</td>
                <td className="px-3 py-2 text-right tabular">{a.not_sold}</td>
                <td className="px-3 py-2 text-right tabular">{a.open}</td>
                <td className="px-3 py-2 text-right tabular">{pct(a.conversion)}</td>
                <td className="px-3 py-2 text-right tabular">{fmtHours(a.median_first_response_hours)}</td>
                <td className="px-3 py-2 text-right tabular">{a.median_messages ?? "—"}</td>
                <td className="px-3 py-2 text-right tabular">{a.median_days_to_close ?? "—"}</td>
                <td className="px-3 py-2 space-y-1 font-normal">
                  <BandRow labels={TIER_BANDS} rows={a.by_band?.budget_tier}/>
                  <BandRow labels={BUDGET_BANDS} rows={a.by_band?.budget_pp_band}/>
                  <BandRow labels={LEAD_TIME_BANDS} rows={a.by_band?.lead_time_band}/>
                  <BandRow labels={RESPONSE_BANDS} rows={a.by_band?.first_response_band}/>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
